import { useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { micromark } from 'micromark';
import { gfmTable, gfmTableHtml } from 'micromark-extension-gfm-table';
// Models
import { IMessage, IMessagePart } from "app/models/ChatAI";

type IRef = IMessagePart['references'][0];
type IMetadata = IRef['metadata'];
type IBboxes = IRef['bboxes'];

export default function useParseMarkdownToHTML(message: IMessage) {
  const { caseId } = useParams<{ caseId: string }>();

  const buildReferenceLink = useCallback(
    (index: number, metadata: IMetadata, bboxes: IBboxes) => {
      const baseUrl = window.location.origin;
      const docPath = `/admin/cases/${caseId}/documents/${metadata.documentId}/${metadata.page}`;
      const params = new URLSearchParams();
      if (bboxes) params.set('bb', bboxes.map((bbox) => bbox.join(',')).join(','));
      const url = `${baseUrl}${docPath}?${params.toString()}`;
      return `[[${index}]](${url})`;
    },
    [caseId]
  );

  const markdownCache = useMemo(() => new Map<string, string>(), []);

  const renderMarkdown = useCallback((text: string) => {
    if (markdownCache.has(text)) {
      return markdownCache.get(text) as string;
    }
    const sanitizedText = text.replace(/^(\.\s*\n)(?=\d+\.\s)/, '');
    const rendered = micromark(sanitizedText || '', {
      extensions: [gfmTable()],
      htmlExtensions: [gfmTableHtml()],
    }) as string;
    markdownCache.set(text, rendered);
    return rendered;
  }, [markdownCache]);

  const combinedMessageText = useMemo(() => {
    if (!message.parts?.length) return message.text || '';
    let refIndex = 0;
    return message.parts
      .map((part: IMessagePart) => {
        if (part.references?.length) {
          const links = part.references.map((ref: IRef) => {
            refIndex++;
            return buildReferenceLink(refIndex, ref.metadata, ref.bboxes);
          });
          return part.text + links.join('');
        }
        return part.text;
      })
      .join('');
  }, [message, buildReferenceLink]);

  return renderMarkdown(combinedMessageText);
}